import {
  clearOnboardingLiveSession,
  finishOnboarding,
  getOnboardingIsNew,
  getOnboardingState,
  isOnboardingLiveSession,
  markOnboardingLiveSession,
  patchOnboardingState,
  type OnboardingSpot,
  type OnboardingState,
} from "./selfSetupStorage";
import { OrganizationType } from "../types/organization.type";

export type SelfSetupTourStep =
  | "materials"
  | "sites"
  | "containers"
  | "pickup"
  | "vehicles"
  | "drivers";

export const SELF_SETUP_TOUR_ORDER: SelfSetupTourStep[] = [
  "materials",
  "sites",
  "containers",
  "pickup",
  "vehicles",
  "drivers",
];

export function getSelfSetupTourOrder(
  organizationType?: OrganizationType | null,
): SelfSetupTourStep[] {
  if (organizationType === OrganizationType.WASTE_OUTLET) {
    return ["materials", "sites", "containers", "pickup"];
  }
  if (organizationType === OrganizationType.TRUCK) {
    return ["vehicles", "drivers"];
  }
  return [];
}

export type SelfSetupTourPhase =
  | "nav"
  | "filters"
  | "map"
  | "table"
  | "addButton"
  | "form"
  | "submit";

/** Default short path used by non-expanded steps. */
export const STEP_TOUR_PHASES: SelfSetupTourPhase[] = ["nav", "addButton"];

/** Materials: sidebar → tables → Add → form → submit → welcome hub. */
export const MATERIALS_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "table",
  "addButton",
  "form",
  "submit",
];

/** Sites: sidebar → filters → tables → Add → form sections → submit → hub. */
export const SITES_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "filters",
  "table",
  "addButton",
  "form",
  "submit",
];

/**
 * Containers: sidebar → filters → map → detail → Add tip → form tabs → submit.
 */
export const CONTAINERS_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "filters",
  "map",
  "table",
  "addButton",
  "form",
  "submit",
];

/** Vehicles (truck orgs): sidebar → search → table → Add → form sections → submit. */
export const VEHICLES_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "filters",
  "table",
  "addButton",
  "form",
  "submit",
];

/** Drivers (truck orgs): sidebar → search → table → Add → form sections → submit. */
export const DRIVERS_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "filters",
  "table",
  "addButton",
  "form",
  "submit",
];

/**
 * Pickup (waste outlet): sidebar → filters → table → Create → form sections → submit.
 */
export const PICKUP_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "filters",
  "table",
  "addButton",
  "form",
  "submit",
];

function phasesForStep(step: SelfSetupTourStep): SelfSetupTourPhase[] {
  if (step === "materials") return MATERIALS_TOUR_PHASES;
  if (step === "sites") return SITES_TOUR_PHASES;
  if (step === "containers") return CONTAINERS_TOUR_PHASES;
  if (step === "pickup") return PICKUP_TOUR_PHASES;
  if (step === "vehicles") return VEHICLES_TOUR_PHASES;
  if (step === "drivers") return DRIVERS_TOUR_PHASES;
  return STEP_TOUR_PHASES;
}

function normalizeStep(raw: string | null | undefined): SelfSetupTourStep | null {
  if (!raw) return null;
  return (SELF_SETUP_TOUR_ORDER as string[]).includes(raw)
    ? (raw as SelfSetupTourStep)
    : null;
}

function normalizePhase(
  step: SelfSetupTourStep,
  raw: string | null | undefined,
): SelfSetupTourPhase {
  const allowed = phasesForStep(step);
  if (raw && (allowed as string[]).includes(raw)) {
    return raw as SelfSetupTourPhase;
  }
  return allowed[0];
}

function filterCompleted(
  completed: string[],
  organizationType?: OrganizationType | null,
): SelfSetupTourStep[] {
  const order = getSelfSetupTourOrder(organizationType);
  return completed
    .map(normalizeStep)
    .filter((s): s is SelfSetupTourStep => s != null && order.includes(s));
}

export function finishSelfSetupTour(
  userId: string,
  organizationId: string,
): void {
  finishOnboarding(userId, organizationId);
}

export function getActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
): SelfSetupTourStep | null {
  const state = getOnboardingState(userId, organizationId);
  if (state.spot === "hub" || state.spot === "congrats") return null;
  return normalizeStep(state.page);
}

export function setActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep,
): void {
  markOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    page: step,
    spot: "nav",
    resumePage: step,
    resumeSpot: "nav",
  });
}

export function pauseOnboardingAtTip(
  userId: string,
  organizationId: string,
  page: SelfSetupTourStep,
  spot: SelfSetupTourPhase,
): void {
  clearOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
    resumePage: page,
    resumeSpot: spot,
  });
}

export function clearActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
): void {
  clearOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
  });
}

export function getSelfSetupTourPhase(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep | null = getActiveSelfSetupTourStep(
    userId,
    organizationId,
  ),
): SelfSetupTourPhase {
  if (!step) return "nav";
  const state = getOnboardingState(userId, organizationId);
  return normalizePhase(step, state.spot);
}

export function setSelfSetupTourPhase(
  userId: string,
  organizationId: string,
  phase: SelfSetupTourPhase,
): void {
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return;
  markOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    page: step,
    spot: phase,
    resumePage: step,
    resumeSpot: phase,
  });
}

export function getCompletedSelfSetupSteps(
  userId: string,
  organizationId: string,
  organizationType?: OrganizationType | null,
): SelfSetupTourStep[] {
  const state = getOnboardingState(userId, organizationId);
  return filterCompleted(state.completed, organizationType);
}

export function markSelfSetupStepCompleted(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep,
  organizationType?: OrganizationType | null,
): SelfSetupTourStep[] {
  const current = getCompletedSelfSetupSteps(
    userId,
    organizationId,
    organizationType,
  );
  const completed = current.includes(step) ? current : [...current, step];
  const stillIncomplete = getFirstIncompleteSelfSetupStep(
    completed,
    organizationType,
  );
  clearOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    completed,
    page: null,
    spot: stillIncomplete ? "hub" : "congrats",
    resumePage: null,
    resumeSpot: undefined,
  });
  return getCompletedSelfSetupSteps(userId, organizationId, organizationType);
}

export function getFirstIncompleteSelfSetupStep(
  completed: SelfSetupTourStep[],
  organizationType?: OrganizationType | null,
): SelfSetupTourStep | null {
  return (
    getSelfSetupTourOrder(organizationType).find(
      (step) => !completed.includes(step),
    ) ?? null
  );
}

export function getNextTourPhase(
  step: SelfSetupTourStep,
  phase: SelfSetupTourPhase,
): SelfSetupTourPhase | null {
  const phases = phasesForStep(step);
  const index = phases.indexOf(phase);
  if (index < 0 || index >= phases.length - 1) return null;
  return phases[index + 1];
}

export function getSelfSetupTourPath(
  step: SelfSetupTourStep,
  _organizationId?: string,
  options?: { showAddHint?: boolean },
): { path: string; state?: { showAddHint?: boolean } } {
  const pathByStep: Record<SelfSetupTourStep, string> = {
    materials: "/recycling/materials",
    sites: "/containers/sites",
    containers: "/containers/list",
    pickup: "/pickup_orders",
    vehicles: "/transportation/fleet",
    drivers: "/transportation/drivers",
  };
  const path = pathByStep[step] ?? "/";
  return options?.showAddHint
    ? { path, state: { showAddHint: true } }
    : { path };
}

export function isSelfSetupTourPath(
  step: SelfSetupTourStep,
  pathname: string,
): boolean {
  return pathname === getSelfSetupTourPath(step).path;
}

export function pauseOnboardingAtHub(
  userId: string,
  organizationId: string,
): void {
  clearOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
  });
}

export function parkActiveOnboardingOnLogout(
  userId: string,
  organizationId: string,
): void {
  if (!getOnboardingIsNew(userId, organizationId)) return;
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) {
    const state = getOnboardingState(userId, organizationId);
    if (state.spot === "congrats") {
      patchOnboardingState(userId, organizationId, {
        page: null,
        spot: "congrats",
        resumePage: null,
        resumeSpot: undefined,
      });
    }
    return;
  }
  const spot = getSelfSetupTourPhase(userId, organizationId, step);
  pauseOnboardingAtTip(userId, organizationId, step, spot);
}

export function parkAllActiveOnboardingForUser(userId: string): void {
  const prefix = `ops.onboarding.isNew.${userId}.`;
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix)) continue;
    if (localStorage.getItem(key) !== "true") continue;
    const organizationId = key.slice(prefix.length);
    if (!organizationId) continue;
    parkActiveOnboardingOnLogout(userId, organizationId);
  }
}

export function parkAllLiveOnboardingTips(): void {
  if (!isOnboardingLiveSession()) return;
  clearOnboardingLiveSession();
}

export function recoverOrphanLiveOnboardingTip(
  userId: string,
  organizationId: string,
): void {
  if (!isOnboardingLiveSession()) return;
  if (!getOnboardingIsNew(userId, organizationId)) {
    clearOnboardingLiveSession();
    return;
  }
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) {
    clearOnboardingLiveSession();
    return;
  }
  const spot = getSelfSetupTourPhase(userId, organizationId, step);
  pauseOnboardingAtTip(userId, organizationId, step, spot);
}

export function getOnboardingResumePoint(
  userId: string,
  organizationId: string,
): { page: SelfSetupTourStep; spot: SelfSetupTourPhase } | null {
  const state = getOnboardingState(userId, organizationId);
  const page = normalizeStep(state.resumePage ?? null);
  if (!page) return null;
  return {
    page,
    spot: normalizePhase(page, state.resumeSpot),
  };
}

export function activateOnboardingResume(
  userId: string,
  organizationId: string,
): { page: SelfSetupTourStep; spot: SelfSetupTourPhase } | null {
  const resume = getOnboardingResumePoint(userId, organizationId);
  if (!resume) return null;
  markOnboardingLiveSession();
  patchOnboardingState(userId, organizationId, {
    page: resume.page,
    spot: resume.spot,
    resumePage: resume.page,
    resumeSpot: resume.spot,
  });
  return resume;
}

export function isOnboardingCongratsPending(
  userId: string,
  organizationId: string,
): boolean {
  if (!getOnboardingIsNew(userId, organizationId)) return false;
  return getOnboardingState(userId, organizationId).spot === "congrats";
}

export type { OnboardingState, OnboardingSpot };
